/**
 * If you are displaying an image from any DB object on any client it should be transformed.
 *
 * You probably either want to use [[optimizeProductImageUrl]] or [[optimizeImageUrl]].
 *
 * ONLY transform **product** or **product option** images using [[optimizeProductImageUrl]]!
 *
 * Details about options can be found in [[IOptimizeImageOptions]]
 *
 * @packageDocumentation
 */
import { IImage } from "@faire/images-api/indigofair/data/IImage";
import clone from "lodash/clone";

import { getCropTagParams } from "./cropTags";
import {
  getFastlyUrl,
  getResizeMethodTag,
  hasBackendOptimizedUrl,
  isTransformableImageUrl,
  modifyBackendUrl,
  shouldUseOptimizedUrl,
} from "./fastly";
import { logger } from "./private/logger";
import { getShopifyImageUrl, isShopifyImageUrl } from "./shopify";
import { IOptimizeImageConfig, IOptimizeImageOptions } from "./types";

/**
 * Whether an IImage has a URL that [[optimizeImageUrl]] can render without error.
 * Includes backend-optimized images that only expose `optimized_url`.
 */
export const hasRenderableImageUrl = (
  image?: Partial<IImage>,
  options: IOptimizeImageOptions = {}
): image is Partial<IImage> => {
  if (!image) {
    return false;
  }
  if (hasBackendOptimizedUrl(image, options)) {
    return true;
  }
  return !!image.url;
};

/**
 * Transform image urls to resize and crop images for use on the client
 *
 * All product images should be encoded using [[optimizeProductImageUrl]]
 */
export const optimizeImageUrl = (
  image: Partial<IImage>,
  isSSR: boolean,
  options: IOptimizeImageOptions = {}
): string | undefined => {
  if (shouldUseOptimizedUrl(image, options)) {
    return modifyBackendUrl(image.optimized_url, isSSR, options, options);
  }
  if (hasBackendOptimizedUrl(image, options) && !image.url) {
    return modifyBackendUrl(image.optimized_url, isSSR, options);
  }
  if (!image) {
    const error = new Error("optimizeImageUrl - invalid ImageOptions argument");
    logger.error(error.message, error, { options });
    return undefined;
  }
  if (!image.url) {
    // A valid image object without a url yet (e.g. still uploading) is a
    // common, benign state throughout the codebase — not worth an error log.
    return image.url;
  }

  if (isShopifyImageUrl(image.url)) {
    return getShopifyImageUrl(image.url, options, isSSR);
  }

  if (!isTransformableImageUrl(image)) {
    // If not transformable short circuit
    return image.url;
  }

  const imageConfig: IOptimizeImageConfig = clone(options);

  const resizeMethodTag = getResizeMethodTag(image.tags);
  if (resizeMethodTag && !options.ignoreResizeTag) {
    imageConfig.resizeMethod = resizeMethodTag;
    if (resizeMethodTag === "pad" && !options.background) {
      imageConfig.background = "#fff";
    }
  }

  const cropTagParams = getCropTagParams(image);
  if (cropTagParams) {
    if (!imageConfig.crop?.smartSquare) {
      imageConfig.crop = cropTagParams;
    }
  }

  if (Array.isArray(image.tags) && image.tags.indexOf("prefer_lossless") >= 0) {
    imageConfig.useLossless = true;
  }

  return getFastlyUrl(image.url, imageConfig, isSSR);
};

/** Quick method for cropping image into a rectangle  */
export const optimizeImageUrlFillRectangle = (
  image: Partial<IImage>,
  isSSR: boolean,
  width: number,
  height: number,
  options: IOptimizeImageOptions = {}
) =>
  optimizeImageUrl(image, isSSR, {
    ...options,
    width,
    height,
    resizeMethod: "fill",
  });

/** Quick method for cropping image into a square  */
export const optimizeImageUrlFillSquare = (
  image: Partial<IImage>,
  isSSR: boolean,
  dimension: number,
  options: IOptimizeImageOptions = {}
) =>
  optimizeImageUrl(image, isSSR, {
    ...options,
    width: dimension,
    height: dimension,
    resizeMethod: "fill",
  });

/** Quick method for resizing image by width */
export const optimizeImageUrlWidth = (
  image: Partial<IImage>,
  isSSR: boolean,
  width: number,
  options: IOptimizeImageOptions = {}
) =>
  optimizeImageUrl(image, isSSR, {
    ...options,
    width,
  });

/** Quick method for resizing image by height */
export const optimizeImageUrlHeight = (
  image: Partial<IImage>,
  isSSR: boolean,
  height: number,
  options: IOptimizeImageOptions = {}
) =>
  optimizeImageUrl(image, isSSR, {
    ...options,
    height,
  });
